import { sizePropValues } from "#ce/composables/useSize";
import { booleanAttribute } from "#ce/utils/props";

const variantOptions = ["outlined", "filled", "double", "chevron"];
const directionOptions = ["up", "down", "left", "right"];

export const props = Object.freeze({
  /**
   * Glyph variant. "outlined" renders an outlined chevron, "filled" renders a solid triangle, "double" renders the stacked up/down indicator used by select-style fields, and "chevron" renders a solid (filled) chevron used for accordion/drilldown rows.
   *
   * @type {String}
   * @default "outlined"
   * @values outlined, filled, double, chevron
   * @storybook-control radio
   */
  variant: {
    type: String,
    default: "outlined",
    validator: (value) => variantOptions.includes(value),
  },
  /**
   * Direction the caret points. Ignored when `expandDirection` is set.
   *
   * @type {String}
   * @default "down"
   * @values up, down, left, right
   * @storybook-control radio
   */
  direction: {
    type: String,
    default: "down",
    validator: (value) => directionOptions.includes(value),
  },
  /**
   * Opt into "expand" rotation: `active` sweeps the caret by 90° between a collapsed and an expanded orientation instead of the default 180°. With "down", the caret points right ("▶") when collapsed and down ("▼") when active. Overrides `direction`.
   *
   * @type {String}
   * @default undefined
   * @storybook-control radio
   */
  expandDirection: {
    type: String,
    default: undefined,
    validator: (value) => value === undefined || ["down"].includes(value),
  },
  /**
   * Flip the caret to indicate an expanded/open state. 180° by default, or 90° when `expandDirection` is set.
   *
   * @type {Boolean|String}
   * @default false
   */
  active: booleanAttribute(),
  /**
   * Animate the rotation between active and inactive states.
   *
   * @type {Boolean|String}
   * @default true
   */
  transitionRotate: booleanAttribute(true),
  /**
   * Glyph size as a design-system token, passed through to the underlying sp-icon so it scales uniformly. For a continuous / passed-through length, set the `--sp-comp-caret-size` custom property instead; the prop wins when both are set.
   *
   * @type {String}
   * @default undefined
   * @storybook-control select
   */
  size: {
    type: String,
    default: undefined,
    validator: (value) => sizePropValues.includes(value),
  },
  /**
   * Fill colour for the caret glyph. Defaults to `currentColor` so the caret inherits the surrounding text colour.
   *
   * @type {String}
   * @default "currentColor"
   */
  color: {
    type: String,
    default: "currentColor",
  },
});
